import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { EQUIPMENT_CATEGORIES } from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { idem } from '../../lib/idempotency';
import { Notice } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Field, field } from '../create/Field';

const CATEGORY_LABEL: Record<string, string> = {
  hvac: 'HVAC',
  water_heater: 'Water heater',
  boiler: 'Boiler',
  plumbing: 'Plumbing',
  electrical: 'Electrical',
  appliance: 'Appliance',
  other: 'Other',
};

type Existing = {
  id: string;
  name: string;
  category: string | null;
  make: string | null;
  model: string | null;
  serial: string | null;
  installedOn: string | null;
  warrantyExpiresOn: string | null;
  laborWarrantyExpiresOn: string | null;
  locationNote: string | null;
  notes: string | null;
};

/**
 * Adding or editing a unit (PRD v2 §6.7). Name first and everything else optional, because
 * the record has to be creatable from a roof with a phone in one hand — a form that demands
 * a model number gets abandoned, and a missing record is worth less than a partial one.
 *
 * Shared by the job screen's tag sheet and the unit's own page, so the two can't drift.
 */
export function EquipmentForm({
  propertyId,
  existing,
  onDone,
  onCancel,
}: {
  propertyId?: string;
  existing?: Existing;
  onDone: () => void;
  onCancel: () => void;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const [name, setName] = useState(existing?.name ?? '');
  const [category, setCategory] = useState(existing?.category ?? '');
  const [make, setMake] = useState(existing?.make ?? '');
  const [model, setModel] = useState(existing?.model ?? '');
  const [serial, setSerial] = useState(existing?.serial ?? '');
  const [installedOn, setInstalledOn] = useState(existing?.installedOn ?? '');
  const [warrantyExpiresOn, setWarranty] = useState(existing?.warrantyExpiresOn ?? '');
  const [laborWarrantyExpiresOn, setLaborWarranty] = useState(
    existing?.laborWarrantyExpiresOn ?? '',
  );
  const [locationNote, setLocationNote] = useState(existing?.locationNote ?? '');
  const [notes, setNotes] = useState(existing?.notes ?? '');

  const done = () => {
    void qc.invalidateQueries();
    onDone();
  };
  const create = useMutation(trpc.equipment.create.mutationOptions({ onSuccess: done }));
  const update = useMutation(trpc.equipment.update.mutationOptions({ onSuccess: done }));

  const busy = create.isPending || update.isPending;
  const error = create.error ?? update.error;
  const canSave = name.trim().length > 0 && !busy;

  const payload = {
    name: name.trim(),
    category: (category || undefined) as 'hvac' | undefined,
    make,
    model,
    serial,
    installedOn,
    warrantyExpiresOn,
    laborWarrantyExpiresOn,
    locationNote,
    notes,
  };

  return (
    <div className="space-y-3">
      {error ? <Notice tone="danger">{error.message}</Notice> : null}

      <Field label="What is it">
        <input
          className={field}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Upstairs condenser"
          autoFocus
        />
      </Field>

      <Field label="Kind">
        <select
          className={field}
          value={category}
          onChange={(e) => setCategory(e.target.value)}
        >
          <option value="">Not saying</option>
          {EQUIPMENT_CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {CATEGORY_LABEL[c] ?? c}
            </option>
          ))}
        </select>
      </Field>

      <div className="grid grid-cols-2 gap-2">
        <Field label="Make">
          <input
            className={field}
            value={make}
            onChange={(e) => setMake(e.target.value)}
            placeholder="Carrier"
          />
        </Field>
        <Field label="Model">
          <input
            className={field}
            value={model}
            onChange={(e) => setModel(e.target.value)}
          />
        </Field>
      </div>

      <Field label="Serial">
        <input
          className={field}
          value={serial}
          onChange={(e) => setSerial(e.target.value)}
          placeholder="Off the plate — leave blank if it's unreadable"
        />
      </Field>

      <Field label="Installed">
        <input
          className={field}
          type="date"
          value={installedOn}
          onChange={(e) => setInstalledOn(e.target.value)}
        />
      </Field>

      <div className="grid grid-cols-2 gap-2">
        <Field label="Parts warranty until">
          <input
            className={field}
            type="date"
            value={warrantyExpiresOn}
            onChange={(e) => setWarranty(e.target.value)}
          />
        </Field>
        <Field label="Your labor warranty until">
          <input
            className={field}
            type="date"
            value={laborWarrantyExpiresOn}
            onChange={(e) => setLaborWarranty(e.target.value)}
          />
        </Field>
      </div>

      <Field label="Where it is">
        <input
          className={field}
          value={locationNote}
          onChange={(e) => setLocationNote(e.target.value)}
          placeholder="Roof, NE corner past the skylight"
        />
      </Field>

      <Field label="Notes">
        <textarea
          className="min-h-[70px] w-full rounded-[var(--radius)] border border-border bg-surface p-2 text-sm"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </Field>

      <div className="flex gap-2">
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          className="ml-auto"
          disabled={!canSave}
          onClick={() =>
            existing
              ? update.mutate({ equipmentId: existing.id, ...payload, ...idem() })
              : create.mutate({ propertyId: propertyId!, ...payload, ...idem() })
          }
        >
          {busy ? 'Saving…' : existing ? 'Save changes' : 'Add this unit'}
        </Button>
      </div>
    </div>
  );
}
